import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Switch,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  DarkMode,
  LightMode,
  Logout,
  Info,
  Person,
  Add,
  Delete
} from '@mui/icons-material';
import { getCurrentConfig, TenantConfig } from '../tenant-config';

interface SettingsProps {
  onLogout: () => void;
  currentUser: string;
  darkMode: boolean;
  onDarkModeChange: (darkMode: boolean) => void;
  tenantConfig: TenantConfig;
}

const Settings: React.FC<SettingsProps> = ({ 
  onLogout, 
  currentUser, 
  darkMode, 
  onDarkModeChange,
  tenantConfig
}) => {
  const [channels, setChannels] = useState<string[]>([]);
  const [newChannel, setNewChannel] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleLogout = () => {
    localStorage.removeItem('loginData');
    onLogout();
  };

  // Fetch channels from column H
  const fetchChannels = useCallback(async () => {
    try {
      setIsLoading(true);
      const range = `${tenantConfig.sheetName}!H:H`;
      const url = `https://sheets.googleapis.com/v4/spreadsheets/1LbMSWz41ukFdbAZFv1q33XgDqwZKFEixBujI77fbwko/values/${range}?key=${tenantConfig.googleApiKey}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.values && data.values.length > 0) {
        // Extract unique channels, skip header row
        const uniqueChannels = Array.from(new Set(
          data.values
            .map((row: any[]) => row[0])
            .filter((ch: string) => ch && ch.trim())
        )) as string[];
        setChannels(uniqueChannels);
      } else {
        setChannels([]);
      }
    } catch (err) {
      console.error('Error fetching channels:', err);
      setError('Không thể tải danh sách channel');
    } finally {
      setIsLoading(false);
    }
  }, [tenantConfig.sheetName, tenantConfig.googleApiKey]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  // Add new channel
  const handleAddChannel = async () => {
    if (!newChannel.trim()) {
      setError('Vui lòng nhập tên channel');
      return;
    }

    if (channels.includes(newChannel.trim())) {
      setError('Channel này đã tồn tại');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      // Call backend API to add channel
      const response = await fetch(tenantConfig.serverApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'addChannel',
          sheetName: tenantConfig.sheetName,
          channel: newChannel.trim()
        })
      });

      if (response.ok) {
        const result = await response.json();
        setSuccess('Đã thêm channel thành công');
        setNewChannel('');
        await fetchChannels();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add channel');
      }
    } catch (err) {
      console.error('Error adding channel:', err);
      setError('Không thể thêm channel. Vui lòng thử lại.');
    } finally {
      setIsLoading(false);
    }
  };

  // Delete channel
  const handleDeleteChannel = async (channelToDelete: string) => {
    try {
      setIsLoading(true);
      setError('');
      
      setError('Tính năng xóa channel cần hỗ trợ từ backend. Vui lòng xóa trực tiếp trong Google Sheets.');
      
    } catch (err) {
      console.error('Error deleting channel:', err);
      setError('Không thể xóa channel');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* User Info */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Person color="primary" />
            <Box>
              <Typography variant="h6">
                {currentUser}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Đã đăng nhập
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Settings */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Cài Đặt
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon>
                {darkMode ? <DarkMode /> : <LightMode />}
              </ListItemIcon>
              <ListItemText 
                primary="Chế độ tối" 
                secondary="Giao diện tối cho mắt dễ chịu"
              />
              <Switch
                checked={darkMode}
                onChange={(e) => onDarkModeChange(e.target.checked)}
                color="primary"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Channel Management */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quản Lý Channel
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {/* Add Channel */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              label="Tên channel mới"
              value={newChannel}
              onChange={(e) => setNewChannel(e.target.value)}
              size="small"
              fullWidth
              disabled={isLoading}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddChannel();
                }
              }}
            />
            <IconButton
              color="primary"
              onClick={handleAddChannel}
              disabled={isLoading || !newChannel.trim()}
              sx={{ 
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': { bgcolor: 'grey.300' }
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : <Add />}
            </IconButton>
          </Box>

          {/* Channel List */}
          <List>
            {channels.length === 0 ? (
              <ListItem>
                <ListItemText 
                  primary="Chưa có channel nào"
                  secondary="Thêm channel mới bằng ô nhập bên trên"
                />
              </ListItem>
            ) : (
              channels.map((channel) => (
                <ListItem
                  key={channel}
                  secondaryAction={
                    <IconButton 
                      edge="end" 
                      aria-label="delete"
                      onClick={() => handleDeleteChannel(channel)}
                      disabled={isLoading}
                      sx={{ color: 'error.main' }}
                    >
                      <Delete />
                    </IconButton>
                  }
                >
                  <ListItemText primary={channel} />
                </ListItem>
              ))
            )}
          </List>
        </CardContent>
      </Card>

      {/* App Info */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Thông Tin Ứng Dụng
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon>
                <Info />
              </ListItemIcon>
              <ListItemText 
                primary="Tên ứng dụng" 
                secondary={getCurrentConfig().appName}
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Info />
              </ListItemIcon>
              <ListItemText 
                primary="Phiên bản" 
                secondary={getCurrentConfig().appVersion}
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Logout */}
      <Card>
        <CardContent>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Logout />}
            onClick={handleLogout}
            fullWidth
            size="large"
          >
            Đăng Xuất
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Settings; 